import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, Receipt, PiggyBank, CheckCircle, TrendingUp } from "lucide-react";
import { createPaycheck, createBill, createSavingsGoal } from "../utils/api";

const STEPS = [
  { id: 1, title: "Add your first paycheck",     Icon: DollarSign, color: "text-primary-600 dark:text-primary-400",   bg: "bg-primary-50 dark:bg-primary-900/30" },
  { id: 2, title: "Add your first bill",          Icon: Receipt,    color: "text-secondary-600 dark:text-secondary-400", bg: "bg-secondary-50 dark:bg-secondary-900/30" },
  { id: 3, title: "Set your first savings goal",  Icon: PiggyBank,  color: "text-purple-600 dark:text-purple-400",    bg: "bg-purple-50 dark:bg-purple-900/30" },
];

const FREQ_OPTS = [
  { value: "weekly",       label: "Weekly" },
  { value: "bi-weekly",    label: "Bi-weekly" },
  { value: "semi-monthly", label: "Semi-monthly (1st & 15th)" },
  { value: "monthly",      label: "Monthly" },
];

const BILL_FREQ = [
  { value: "weekly",   label: "Weekly" },
  { value: "monthly",  label: "Monthly" },
  { value: "annual",   label: "Annual" },
  { value: "one-time", label: "One-time" },
];

const CATEGORIES = [
  "Housing","Utilities","Subscriptions","Transportation",
  "Groceries","Insurance","Entertainment","Medical","Other",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [paycheck, setPaycheck] = useState({ label: "", spouseName: "", amount: "", frequency: "bi-weekly" });
  const [bill, setBill]         = useState({ name: "", amount: "", dueDate: "", frequency: "monthly", category: "Housing" });
  const [goal, setGoal]         = useState({ name: "", targetAmount: "", currentAmount: "", targetDate: "" });

  async function handleStep1(e) {
    e.preventDefault();
    setError("");
    if (!paycheck.label || !paycheck.amount) { setError("Name and amount are required."); return; }
    setLoading(true);
    try { await createPaycheck(paycheck); setStep(2); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleStep2(e) {
    e.preventDefault();
    setError("");
    if (!bill.name || !bill.amount || !bill.category) { setError("Name, amount, and category are required."); return; }
    setLoading(true);
    try { await createBill(bill); setStep(3); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleStep3(e) {
    e.preventDefault();
    setError("");
    if (!goal.name || !goal.targetAmount) { setError("Name and target amount are required."); return; }
    setLoading(true);
    try { await createSavingsGoal(goal); navigate("/dashboard", { replace: true }); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const current = STEPS[step - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2 mb-6">
          <TrendingUp className="w-7 h-7 text-white" />
          <span className="text-2xl font-bold text-white">FinancePlan</span>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {STEPS.map((s) => (
            <div key={s.id} className="flex items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                s.id < step  ? "bg-white text-primary-600"
                : s.id === step ? "bg-white text-primary-600 ring-2 ring-white ring-offset-2 ring-offset-primary-600"
                : "bg-white/30 text-white"
              }`}>
                {s.id < step ? <CheckCircle className="w-4 h-4" /> : s.id}
              </div>
              {s.id < STEPS.length && (
                <div className={`w-10 h-0.5 ${s.id < step ? "bg-white" : "bg-white/30"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2.5 rounded-xl ${current.bg}`}>
              <current.Icon className={`w-6 h-6 ${current.color}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Step {step} of {STEPS.length}</p>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{current.title}</h2>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Paycheck label *</label>
                  <input className="input" placeholder="e.g. Alex's Job" value={paycheck.label}
                    onChange={(e) => setPaycheck((p) => ({ ...p, label: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Earner name</label>
                  <input className="input" placeholder="e.g. Alex" value={paycheck.spouseName}
                    onChange={(e) => setPaycheck((p) => ({ ...p, spouseName: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Amount *</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="0.00" value={paycheck.amount}
                    onChange={(e) => setPaycheck((p) => ({ ...p, amount: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Frequency *</label>
                  <select className="input" value={paycheck.frequency}
                    onChange={(e) => setPaycheck((p) => ({ ...p, frequency: e.target.value }))}>
                    {FREQ_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? "Saving…" : "Next Step"}
              </button>
              <button type="button" onClick={() => navigate("/dashboard")}
                className="w-full text-center text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                Skip setup — go to dashboard
              </button>
            </form>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <form onSubmit={handleStep2} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Bill name *</label>
                  <input className="input" placeholder="e.g. Rent" value={bill.name}
                    onChange={(e) => setBill((b) => ({ ...b, name: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Amount *</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="0.00" value={bill.amount}
                    onChange={(e) => setBill((b) => ({ ...b, amount: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Category *</label>
                  <select className="input" value={bill.category}
                    onChange={(e) => setBill((b) => ({ ...b, category: e.target.value }))}>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Frequency *</label>
                  <select className="input" value={bill.frequency}
                    onChange={(e) => setBill((b) => ({ ...b, frequency: e.target.value }))}>
                    {BILL_FREQ.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Due date</label>
                <input className="input" type="date" value={bill.dueDate}
                  onChange={(e) => setBill((b) => ({ ...b, dueDate: e.target.value }))} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1 justify-center">Back</button>
                <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center py-2.5">
                  {loading ? "Saving…" : "Next Step"}
                </button>
              </div>
            </form>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <form onSubmit={handleStep3} className="space-y-4">
              <div>
                <label className="label">Goal name *</label>
                <input className="input" placeholder="e.g. Emergency Fund" value={goal.name}
                  onChange={(e) => setGoal((g) => ({ ...g, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Target amount *</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="10000" value={goal.targetAmount}
                    onChange={(e) => setGoal((g) => ({ ...g, targetAmount: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Already saved</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="0.00" value={goal.currentAmount}
                    onChange={(e) => setGoal((g) => ({ ...g, currentAmount: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label">Target date</label>
                <input className="input" type="date" value={goal.targetDate}
                  onChange={(e) => setGoal((g) => ({ ...g, targetDate: e.target.value }))} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1 justify-center">Back</button>
                <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center py-2.5">
                  {loading ? "Saving…" : "Go to Dashboard"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
